import * as THREE from 'three';
import { gsap, CSSPlugin } from 'gsap/all';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import _ from 'lodash';
import autoBind from 'auto-bind';
import Bubble from './components/Bubble/Bubble';
import SONGS from './SONGS';
import InfiniteScrollGallery from './components/InfiniteScrollGallery';
import getSizeAtDepth from './utils/getSizeAtDepth';
import HighlightedBubble from './components/HighlightedBubble';

gsap.registerPlugin(CSSPlugin);
const SCENE_SELECTOR = 'canvas.webgl';

const mobileAndTabletCheck = () => {
  let check = false;
  (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; }(navigator.userAgent || navigator.vendor || window.opera));
  return check;
};

class App {
  clock = new THREE.Clock();

  scene = new THREE.Scene();

  canvas = document.querySelector(SCENE_SELECTOR);

  renderer = new THREE.WebGLRenderer({
    canvas: this.canvas,
    antialias: true,
  });

  camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight);

  raycaster = new THREE.Raycaster();

  mouse = new THREE.Vector2();

  paused = true;

  audioListener = new THREE.AudioListener();

  TITLE_BUBBLE = {
    noise: 1.4,
    hoverRadius: 0.5,
    messageStart: 200,
    messageEnd: 750,
    onClick: _.throttle(() => this.switchPause(), 400, { trailing: false }),
  };

  ARTIST_BUBBLE = {
    roughness: 0.6, messageStart: 300, messageEnd: 600, entryAnimation: true,
  };

  titleBubbles = [];

  scrollGalleryPosition = -6;

  loader = document.querySelector('#loader');

  isMobile = mobileAndTabletCheck();

  constructor() {
    autoBind(this);
    this.setUpCamera();

    this.onResize();
    window.addEventListener('resize', this.onResize);

    this.setUpScene().then();
  }

  onLoad() {
    window.addEventListener('pointermove', this.onMouseMove);
    this.loader.classList.add('load_animation_end');
    this.tick();
  }

  switchPause() {
    this.paused = !this.paused;

    if (!this.currentSong.sound) return;

    if (this.paused) this.currentSong.sound.pause();
    else this.currentSong.sound.play();
  }

  setUpCamera() {
    this.scene.add(this.camera);
    this.camera.add(this.audioListener);
  }

  async setUpScene() {
    this.scene.background = new THREE.Color(0x000000);
    this.environment = new RoomEnvironment();
    this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);

    this.scene.environment = this.pmremGenerator.fromScene(this.environment).texture;

    await this.preloadMusic();

    this.scrollGallery = new InfiniteScrollGallery({
      camera: this.camera,
      scrollableChildren: this.titleBubbles,
      onCentredObject: this.setSong,
      bounds: [-1, 1],
    });

    this.onLoad();

    this.renderer.shadowMap.enabled = true;
  }

  async preloadMusic() {
    return new Promise((res) => {
      let loaded = 0;
      SONGS.forEach(async (song, index) => {
        const y = getSizeAtDepth(this.camera, this.scrollGalleryPosition)[1];
        await this.createInteractiveBubble({
          ...song,
          posY: index * y * 0.75,
          posZ: this.scrollGalleryPosition,
          index,
          ...this.TITLE_BUBBLE,
        });
        loaded += 1;
        if (loaded === SONGS.length - 1) res(true);
      });
    });
  }

  setSong(song) {
    if (this.currentSong === song) return;

    if (this.currentSong?.sound) {
      this.currentSong.sound.pause();
      this.loadSound(this.currentSong, this.currentSong.source);
    }

    this.currentSong = song;
    this.resetArtist(this.currentSong.userData.artist);

    if (!this.currentSong?.sound) return;

    if (this.currentSong.sound) this.soundAnalyser = new THREE.AudioAnalyser(this.currentSong.sound, 32);
    if (!this.paused && this.currentSong.sound) this.currentSong.sound.play();
  }

  setArtist(artist) {
    this.artistBubble = new Bubble({ displayMessage: artist, ...this.ARTIST_BUBBLE });
    this.artistBubble.scale.set(-6, 6, 6);
    this.scene.add(this.artistBubble);
    this.artistBubble.show(false);
  }

  loadSound(bubble, source, res = () => {}) {
    if (this.isMobile) res(true);
    const sound = new THREE.Audio(this.audioListener);
    const loader = new THREE.AudioLoader();
    loader.load(source, (buffer) => {
      sound.setBuffer(buffer);
      sound.setLoop(true);
      bubble.setSound(sound);
      bubble.source = source;
      res(true);
    });
  }

  async createInteractiveBubble(props) {
    return new Promise((res) => {
      const bubble = new HighlightedBubble(props);

      this.loadSound(bubble, props.source, res);

      this.scene.add(bubble);
      this.titleBubbles.push(bubble);
    });
  }

  resetArtist(artist) {
    if (this.artistBubble) {
      this.artistBubble.hide().then(() => {
        this.scene.remove(this.artistBubble);
        this.setArtist(artist);
      });
    } else this.setArtist(artist);
  }

  tick() {
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const elapsedTime = this.clock.getElapsedTime();

    if (this.soundAnalyser && !this.paused) {
      this.currentSong.bubble.setNoise(this.soundAnalyser.getAverageFrequency() / 64);
    }
    this.titleBubbles.forEach((b) => b.bubble.tick(elapsedTime + b.userData.songIndex, !this.paused));

    this.artistBubble?.tick(elapsedTime);
    this.renderer.render(this.scene, this.camera);

    requestAnimationFrame(this.tick);
  }

  onResize() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;

    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.renderer.setSize(window.innerWidth, window.innerHeight);

    this.camera.updateProjectionMatrix();
  }

  onMouseMove(evt) {
    evt.preventDefault();
    const array = this.getMousePosition(evt.clientX, evt.clientY);
    this.mouse.fromArray(array);

    this.titleBubbles.forEach((bubble) => {
      if (bubble === this.scrollGallery.selected) {
        const intersects = this.getIntersect(this.mouse, bubble);

        if (intersects.length && intersects[0].point) {
          bubble.bubble.intersectPoint(intersects[0].point);
        } else bubble.bubble.cancelIntersection();
      }
    });
  }

  getMousePosition(x, y) {
    const rect = this.canvas.getBoundingClientRect();
    return [(x - rect.left) / rect.width, (y - rect.top) / rect.height];
  }

  getIntersect(point, object) {
    this.mouse.set((point.x * 2) - 1, -(point.y * 2) + 1);
    this.raycaster.setFromCamera(this.mouse, this.camera);
    return this.raycaster.intersectObject(object, true);
  }
}

export default App;
